import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { Hub } from 'aws-amplify/utils';
import { HubCapsule } from 'aws-amplify-react-native/types';
import { GetChirurgienQuery, useGetChirurgienLazyQuery } from '@/data/data';
import { ArrayElement } from '@/utils/typeHelpers';
import { useAuthContext } from '@/components/Auth/AuthContext';

type UserContextProps = {
  user?: ArrayElement<GetChirurgienQuery['chirurgien']>;
  reloadUser: () => Promise<void>;
  userIsLoading: boolean;
};

const UserContext = createContext<UserContextProps>({
  user: undefined,
  reloadUser: async () => {},
  userIsLoading: true,
});

const UserProvider: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const authUser = useAuthContext();
  const [logged, setLogged] = useState<boolean>(false);
  const [loadUser, { loading: loadingUser, data: queryData }] = useGetChirurgienLazyQuery();

  const currentUser = async () => {
    await loadUser({
      variables: {
        cognitoToken: authUser?.cognitoUser?.userId,
      },
    });
    setLogged(true);
  };

  const authListener = async ({ payload: { event } }: HubCapsule) => {
    console.log(event);
    switch (event) {
      case 'signedIn':
        await currentUser();
        break;
      case 'signedOut':
        setLogged(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    currentUser();
    const listener = Hub.listen('auth', authListener);
    return () => listener();
  }, []);

  useEffect(() => {
    currentUser();
  }, [authUser?.cognitoUser?.userId]);

  const values = useMemo(() => {
    return {
      user: logged ? queryData?.chirurgien[0] : undefined,
      reloadUser: currentUser,
      userIsLoading: loadingUser,
    };
  }, [loadingUser, queryData?.chirurgien?.[0]?.id, logged]);

  return <UserContext.Provider value={values}>{children}</UserContext.Provider>;
};

const useUserContext = () => useContext(UserContext);

export { useUserContext, UserContext, UserProvider };
